import {
  QueryConstraint,
  WhereFilterOp,
  limit,
  orderBy,
  startAfter,
  where,
} from 'firebase/firestore';

import { SCHEDULE_TYPES } from '@src/constants/commons';
import { FSCollections } from '@src/constants/database';
import type {
  ElasticQueryFilters,
  ElasticQueryParams,
} from '@src/models/interfaces/general/ElasticQueryParams.interface';
import type { ElasticSearchResponse } from '@src/models/interfaces/general/ElasticSearchResponseInterface';
import { ScheduleMapper } from '@src/models/mappers/schedule.mapper';
import type { ScheduleSearchIdsResult } from '@src/models/mappers/schedule.mapper';
import type {
  DeleteScheduleParams,
  DeleteScheduleResponse,
  ScheduleFormSaveContext,
  ScheduleOptionAction,
  ScheduleSaveInput,
  ScheduleSearchSummariesParams,
  ScheduleSearchSummary,
} from '@src/models/interfaces/schedule.interface';
import type TSchedule from '@src/models/interfaces/schedule.interface';
import { ScheduleType } from '@src/models/interfaces/schedule.interface';
import { TEventTypeSummary } from '@src/models/interfaces/eventTypeSummary.interface';
import TScheduleTrigger from '@src/models/interfaces/scheduleTrigger.interface';
import ScheduleRepository from '@src/repositories/ScheduleRepository';
import { buildPartialMatch } from '@src/util/elastic/buildPartialMatch';
import safeExecute from '@src/util/safeExecute';

import { buildTextSearch } from './utils';

const SUMMARY_PAGE_SIZE = 8;
const SUMMARY_FIELDS = ['id', 'divId', 'name', 'type', 'schedule.startAt'];

interface IListScheduleParams {
  orgId: string;
  divsIds: string[];
  pageSize?: number;
  from?: number;
  searchTerm?: string;
  uid?: string;
  memberUserIds?: string[];
  dateRange?: { gte?: string; lte?: string };
  type?: string;
  exactMatch?: boolean;
}

interface ISearchScheduleIdsParams {
  orgId: string;
  divsIds: string[];
  pageSize?: number;
  from?: number;
  searchTerm?: string;
  memberUserIds?: string[];
  dateRange?: { gte?: string; lte?: string };
  type?: string;
  exactMatch?: boolean;
}

interface ISearchScheduleParams {
  orgId: string;
  divsIds: string[];
  pageSize?: number;
  from?: number;
  searchTerm?: string;
  type?: string;
  ids?: string[];
  exactMatch?: boolean;
  /** Assigned members, matched directly or through one of their groups. */
  memberUserIds?: string[];
}

/**
 * `members.users` and `members.groups.users` are nested in the schedules index,
 * so assigned users only match inside their own nested scope.
 */
const buildAssignedMemberFilters = (memberUserIds: string[]): ElasticQueryFilters => ({
  should: [
    {
      nested: {
        path: 'members.users',
        query: { terms: { 'members.users.id': memberUserIds } },
      },
    },
    {
      nested: {
        path: 'members.groups.users',
        query: { terms: { 'members.groups.users.id': memberUserIds } },
      },
    },
  ],
  minimumShouldMatch: 1,
});

interface IListFirestoreParams {
  orgId: string;
  divIds: string[];
  where?: string[][];
  orderBy?: (string | [string, 'asc' | 'desc'])[];
  limit?: number;
  startAfter?: unknown;
}

interface ISaveScheduleServiceParams {
  organizationId: string;
  divisionId: string;
  formContext: ScheduleFormSaveContext;
  scheduleId?: string;
  isClone?: boolean;
  executionOverrideAction?: ScheduleOptionAction | null;
  existingSchedule?: TSchedule | null;
  scheduleType?: ScheduleType;
}

interface ISaveScheduleInputParams {
  organizationId: string;
  divisionId: string;
  schedule: ScheduleSaveInput;
  scheduleId?: string;
  executionOverrideAction?: ScheduleOptionAction | null;
}

class ScheduleService {
  private repository: ScheduleRepository;

  constructor() {
    this.repository = new ScheduleRepository();
  }

  async list({
    orgId,
    divsIds,
    pageSize = 10,
    from = 0,
    searchTerm = '',
    uid = '',
    memberUserIds = [],
    dateRange = {},
    type = SCHEDULE_TYPES.schedule,
    exactMatch = false,
  }: IListScheduleParams): Promise<ElasticSearchResponse<TSchedule> | null> {
    return safeExecute(
      async () => {
        if (!orgId) throw new Error('Organization ID is required');

        const sort = [{ field: 'createdAt._seconds', order: 'desc' as const }];
        const pagination = { from, size: pageSize };
        const term: Record<string, unknown> = { type };

        if (uid) term.uid = uid;

        const filters: ElasticQueryParams['filters'] = { term };
        if (searchTerm) filters.textSearch = buildTextSearch(searchTerm, exactMatch);
        if (memberUserIds.length) {
          Object.assign(filters, buildAssignedMemberFilters(memberUserIds));
        }

        if (dateRange.gte || dateRange.lte) {
          filters.range = {
            ...filters.range,
            'schedule.startAt._seconds': {
              ...(dateRange.gte && { gte: dateRange.gte }),
              ...(dateRange.lte && { lte: dateRange.lte }),
            },
          };
        }

        const params: ElasticQueryParams = {
          entity: FSCollections.SCHEDULES,
          organizationId: orgId,
          divisionIds: divsIds,
          filters,
          pagination,
          sort,
        };

        return this.repository.list(params);
      },
      'ScheduleService.list',
      { orgId, pageSize, from },
    );
  }

  /**
   * Resolves schedule document ids (and their division) matching the list filters.
   *
   * The ids source for `useElasticFirestoreList`: Elastic decides which schedules and in
   * what order, Firestore materializes them. `fields` keeps `_source` out of the response —
   * this is an id lookup, not a document fetch — so asking for slack ids is nearly free.
   */
  async searchIds({
    orgId,
    divsIds,
    pageSize = 10,
    from = 0,
    searchTerm = '',
    memberUserIds = [],
    dateRange = {},
    type = SCHEDULE_TYPES.schedule,
    exactMatch = true,
  }: ISearchScheduleIdsParams): Promise<ScheduleSearchIdsResult | null> {
    return safeExecute(
      async () => {
        if (!orgId) throw new Error('Organization ID is required');

        const filters: ElasticQueryParams['filters'] = { term: { type } };

        if (searchTerm) filters.textSearch = buildTextSearch(searchTerm, exactMatch);
        if (memberUserIds.length) {
          Object.assign(filters, buildAssignedMemberFilters(memberUserIds));
        }
        if (dateRange.gte || dateRange.lte) {
          filters.range = {
            'schedule.startAt._seconds': {
              ...(dateRange.gte && { gte: dateRange.gte }),
              ...(dateRange.lte && { lte: dateRange.lte }),
            },
          };
        }

        const params: ElasticQueryParams = {
          entity: FSCollections.SCHEDULES,
          organizationId: orgId,
          divisionIds: divsIds,
          filters,
          pagination: { from, size: pageSize },
          // Single sort field, matching `list()` and every other list in the app.
          //
          // A tiebreaker would make page windows fully deterministic (schedules created in
          // the same second can otherwise swap between two queries, so a row may repeat or
          // be skipped at a page boundary). It is not available: `id` is not whitelisted for
          // sorting, and adding the whitelisted `name` as a second sort field made the API
          // fail with an internal error — being whitelisted does not mean the index can sort
          // by it. Recovering determinism is a backend change, not a client one.
          sort: [{ field: 'createdAt._seconds', order: 'desc' }],
          // Requested fields are whitelisted per entity too, and rejected the same way
          // (`Invalid fields requested`). Schedules expose their division as `divId`.
          fields: ['id', 'divId'],
        };

        const response = await this.repository.list(params);
        return ScheduleMapper.fromElasticSearchIdsResponse(response);
      },
      'ScheduleService.searchIds',
      { orgId, pageSize, from },
    );
  }

  /**
   * Latest schedules of the given types whose name contains every typed token,
   * with just enough fields to label a result row (global sidebar search).
   */
  async searchSummaries({
    orgId,
    divsIds,
    searchTerm,
    types,
    pageSize = SUMMARY_PAGE_SIZE,
  }: ScheduleSearchSummariesParams): Promise<ScheduleSearchSummary[]> {
    return safeExecute(
      async () => {
        if (!orgId) throw new Error('Organization ID is required');

        const textSearch = buildPartialMatch(['name'], searchTerm);
        if (!textSearch || !types.length) return [];

        const params: ElasticQueryParams = {
          entity: FSCollections.SCHEDULES,
          organizationId: orgId,
          divisionIds: divsIds,
          filters: { terms: { type: types }, textSearch },
          pagination: { from: 0, size: pageSize },
          sort: [{ field: 'createdAt._seconds', order: 'desc' }],
          fields: SUMMARY_FIELDS,
        };

        const response = await this.repository.list(params);
        return ScheduleMapper.fromElasticSearchSummariesResponse(response);
      },
      'ScheduleService.searchSummaries',
      { orgId, pageSize, types },
    );
  }

  async search({
    orgId,
    divsIds,
    pageSize = 10,
    from = 0,
    searchTerm = '',
    type = SCHEDULE_TYPES.schedule,
    ids = [],
    exactMatch = false,
    memberUserIds = [],
  }: ISearchScheduleParams): Promise<ElasticSearchResponse<TSchedule> | null> {
    return safeExecute(
      async () => {
        if (!orgId) throw new Error('Organization ID is required');

        const sort = [{ field: 'createdAt._seconds', order: 'desc' as const }];
        const pagination = { from, size: pageSize };
        const filters: ElasticQueryParams['filters'] = { term: { type } };

        if (searchTerm) filters.textSearch = buildTextSearch(searchTerm, exactMatch);
        if (ids?.length > 0) filters.terms = { id: ids };
        if (memberUserIds.length) {
          Object.assign(filters, buildAssignedMemberFilters(memberUserIds));
        }

        const params: ElasticQueryParams = {
          entity: FSCollections.SCHEDULES,
          organizationId: orgId,
          divisionIds: divsIds,
          filters,
          pagination,
          sort,
        };

        return this.repository.list(params);
      },
      'ScheduleService.search',
      { orgId, pageSize, from },
    );
  }

  async listFirestore({
    orgId,
    divIds = [],
    where: whereConditions = [],
    orderBy: orderByConditions = [],
    limit: limitValue,
    startAfter: startAfterDoc,
  }: IListFirestoreParams): Promise<Array<{ id: string } & Record<string, unknown>>> {
    return safeExecute(
      async () => {
        if (!orgId) throw new Error('orgId is required');
        if (!divIds?.length) throw new Error('divIds must be a non-empty array');

        const queryConstraints = this._buildQueryConstraints({
          where: whereConditions,
          orderBy: orderByConditions,
          limit: limitValue,
          startAfter: startAfterDoc,
        });

        return this.repository.listFirestore({ orgId, divIds, queryConstraints });
      },
      'ScheduleService.listFirestore',
      { orgId, divIds },
    );
  }

  async delete({
    organizationId,
    divisionId,
    scheduleId,
  }: DeleteScheduleParams): Promise<DeleteScheduleResponse> {
    return safeExecute(
      async () => {
        if (!organizationId || !divisionId || !scheduleId) {
          throw new Error('organizationId, divisionId, and scheduleId are required');
        }

        return this.repository.delete({ organizationId, divisionId, scheduleId });
      },
      'ScheduleService.delete',
      { organizationId, divisionId, scheduleId },
    );
  }

  async save({
    organizationId,
    divisionId,
    formContext,
    scheduleId,
    isClone = false,
    executionOverrideAction = null,
    existingSchedule = null,
    scheduleType = ScheduleType.Schedule,
  }: ISaveScheduleServiceParams): Promise<TSchedule> {
    return safeExecute(
      async () => {
        if (!organizationId || !divisionId) {
          throw new Error('organizationId and divisionId are required');
        }

        const { values } = formContext;
        if (!values.name?.trim()) {
          throw new Error('Schedule name is required');
        }

        const resolvedScheduleId =
          scheduleId && !isClone
            ? scheduleId
            : this.repository.newScheduleId(organizationId, divisionId);

        let triggers = formContext.triggers;
        if (isClone) {
          triggers = ScheduleMapper.regenerateTriggerIdsForClone(triggers, () =>
            this.repository.newTriggerId(),
          );
        }

        const enrichedTriggers = await this.enrichTriggersWithEventTypes(
          organizationId,
          divisionId,
          triggers,
        );

        const parsedTriggers = ScheduleMapper.applyTriggersFactory(
          enrichedTriggers,
          values.triggersFollowIndex,
        ).map(trigger => ScheduleMapper.toSaveTrigger(trigger));

        const scheduleInput = ScheduleMapper.toSaveInput({
          scheduleId: resolvedScheduleId,
          divisionId,
          formContext,
          triggers: parsedTriggers,
          existingSchedule: scheduleId && !isClone ? existingSchedule : null,
          isUpdate: Boolean(scheduleId && !isClone),
          scheduleType,
        });

        if (executionOverrideAction) {
          await this.repository.createScheduleOption({
            organizationId,
            divisionId,
            scheduleId: resolvedScheduleId,
            action: executionOverrideAction,
          });
        }

        const response = await this.repository.callSave({
          organizationId,
          divisionId,
          schedule: scheduleInput,
        });

        if (!response?.success) {
          throw new Error('Failed to save schedule');
        }

        return ScheduleMapper.toSavedScheduleSummary(scheduleInput, response.scheduleId);
      },
      'ScheduleService.save',
      { organizationId, divisionId, scheduleId },
    );
  }

  /**
   * Persists a schedule when the payload is already a {@link ScheduleSaveInput}
   * (e.g. built by a domain mapper such as WorkOrderMapper).
   * For form-driven flows (schedules, checkpoint routes), use {@link save} instead.
   */
  async saveScheduleInput({
    organizationId,
    divisionId,
    schedule,
    scheduleId,
    executionOverrideAction = null,
  }: ISaveScheduleInputParams): Promise<TSchedule> {
    return safeExecute(
      async () => {
        if (!organizationId || !divisionId) {
          throw new Error('organizationId and divisionId are required');
        }

        if (!schedule.name?.trim()) {
          throw new Error('Schedule name is required');
        }

        const resolvedScheduleId =
          scheduleId ??
          (schedule.id?.trim()
            ? schedule.id
            : this.repository.newScheduleId(organizationId, divisionId));

        const scheduleInput: ScheduleSaveInput = {
          ...schedule,
          id: resolvedScheduleId,
          divId: divisionId,
        };

        if (executionOverrideAction) {
          await this.repository.createScheduleOption({
            organizationId,
            divisionId,
            scheduleId: resolvedScheduleId,
            action: executionOverrideAction,
          });
        }

        const response = await this.repository.callSave({
          organizationId,
          divisionId,
          schedule: scheduleInput,
        });

        if (!response?.success) {
          throw new Error('Failed to save schedule');
        }

        return ScheduleMapper.toSavedScheduleSummary(scheduleInput, response.scheduleId);
      },
      'ScheduleService.saveScheduleInput',
      { organizationId, divisionId, scheduleId },
    );
  }

  private async enrichTriggersWithEventTypes(
    organizationId: string,
    divisionId: string,
    triggers: TScheduleTrigger[],
  ): Promise<TScheduleTrigger[]> {
    return Promise.all(
      triggers.map(async trigger => {
        const eventType = await this.resolveEventType(
          organizationId,
          divisionId,
          trigger.eventType,
        );
        return {
          ...trigger,
          eventType,
        };
      }),
    );
  }

  private async resolveEventType(
    organizationId: string,
    divisionId: string,
    eventType: TEventTypeSummary | string | undefined,
  ): Promise<TEventTypeSummary | undefined> {
    if (!eventType) {
      return undefined;
    }

    if (typeof eventType === 'string') {
      return this.fetchEventTypeSummary(organizationId, divisionId, eventType);
    }

    const eventTypeId = eventType.id;
    if (!eventTypeId) {
      return undefined;
    }

    const hasSummaryShape = Boolean(
      eventType.name && eventType.icon && eventType.color && eventType.components,
    );

    if (hasSummaryShape) {
      return ScheduleMapper.toEventTypeSummary(eventType);
    }

    return this.fetchEventTypeSummary(organizationId, divisionId, eventTypeId);
  }

  private async fetchEventTypeSummary(
    organizationId: string,
    divisionId: string,
    eventTypeId: string,
  ): Promise<TEventTypeSummary> {
    const eventData = await this.repository.getEventTypeById(
      organizationId,
      divisionId,
      eventTypeId,
    );

    if (!eventData) {
      throw new Error("Event Type doesn't exits");
    }

    return ScheduleMapper.toEventTypeSummary(eventData);
  }

  private _buildQueryConstraints({
    where: whereConditions = [],
    orderBy: orderByConditions = [],
    limit: limitValue,
    startAfter: startAfterDoc,
  }: Pick<IListFirestoreParams, 'where' | 'orderBy' | 'limit' | 'startAfter'>): QueryConstraint[] {
    const queryConstraints: QueryConstraint[] = [];

    whereConditions.forEach(([field, operator, value]) => {
      queryConstraints.push(where(field, operator as WhereFilterOp, value));
    });

    orderByConditions.forEach(ord => {
      const [field, direction = 'asc'] = Array.isArray(ord) ? ord : [ord, 'asc'];
      queryConstraints.push(orderBy(field, direction as 'asc' | 'desc'));
    });

    if (startAfterDoc) queryConstraints.push(startAfter(startAfterDoc));
    if (limitValue) queryConstraints.push(limit(limitValue));

    return queryConstraints;
  }
}

export default ScheduleService;
